/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';
import CheckBox from '../../../../atoms/Checkbox/Checkbox';
import {
    PriceFormValues,
    RefundChoice,
    RefundChoices,
} from '../../../../../services/helpers/merchantPrices';

interface RefundFeeFieldsProps {
    /** The loaded choices, for what the view shows. */
    defaults: RefundChoices;
    canEdit: boolean;
}

const choices: { choice: RefundChoice; label: string; testId: string }[] = [
    {
        choice: 'serviceFee',
        label: 'Charge the service fee on refunds',
        testId: 'refund-service-fee',
    },
    {
        choice: 'transactionFee',
        label: 'Charge the transaction fee on refunds',
        testId: 'refund-transaction-fee',
    },
];

/**
 * Which purchase fees are charged on refunds too, for every market and payment method. A checked fee is sent as each
 * method's refund fee when the list is saved.
 */
const RefundFeeFields: React.FC<RefundFeeFieldsProps> = ({
    defaults,
    canEdit,
}: RefundFeeFieldsProps) => {
    const { register } = useFormContext<PriceFormValues>();

    return (
        <Panel role="group" aria-labelledby="refund-fees-title" data-testid="refund-fees">
            <Title id="refund-fees-title">Refund fees</Title>
            <Note>
                {canEdit
                    ? 'Part of the price list: applies to every market and payment method, and is stored when you save.'
                    : 'Part of the price list: applies to every market and payment method.'}
            </Note>
            <Choices>
                {choices.map(({ choice, label, testId }) => {
                    if (!canEdit)
                        return (
                            <span key={choice}>
                                {label}:{' '}
                                <span data-testid={testId}>{defaults[choice] ? 'Yes' : 'No'}</span>
                            </span>
                        );

                    const { ref, ...registered } = register(`refunds.${choice}`);

                    return (
                        <CheckBox
                            key={choice}
                            id={testId}
                            noMargin
                            dataTestId={testId}
                            setRef={ref}
                            {...registered}
                        >
                            {label}
                        </CheckBox>
                    );
                })}
            </Choices>
        </Panel>
    );
};

/** Set apart from the view filters above it: these are prices, not display settings. */
const Panel = styled.div`
    padding: 1.2rem 1.6rem;
    border: 0.1rem solid ${props => props.theme.colors.border};
    border-left: 0.3rem solid ${props => props.theme.colors.primary};
    border-radius: 0.4rem;
    background: ${props => props.theme.colors.subtle.light};
    font-size: ${props => props.theme.text.size.small};
`;

const Title = styled.div`
    font-family: ${props => props.theme.text.font.medium};
`;

const Note = styled.p`
    margin: 0.2rem 0 0.8rem;
    color: ${props => props.theme.colors.text.secondary};
`;

const Choices = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem 2rem;
`;

export default RefundFeeFields;
